import { createStore } from "vuex";
import bootstrap from "bootstrap/dist/js/bootstrap.min.js";
import createPersistedState from "vuex-persistedstate";
import { isTokenExpired } from "../t_js_resources/token_decode.js";

const storage = window.localStorage;

export default createStore({
  state: {
    token: null,
    user: null,
    permissions: [],
    company: "",
    companyActive: "",
    logo: "",
    time: "",
    ttl: "",
    is_querying_timer: "",

    // inactivity
    inactivityTimer: null,

    package_items: [],

    // ui
    hideConfigButton: false,
    isPinned: true,
    showConfig: false,
    isTransparent: "",
    isRTL: false,
    color: "",
    isNavFixed: false,
    isAbsolute: false,
    showNavs: true,
    showSidenav: true,
    showNavbar: true,
    showFooter: true,
    showMain: true,
    navbarFixed:
      "position-sticky blur shadow-blur left-auto top-1 z-index-sticky px-0 mx-4",
    absolute: "position-absolute px-4 mx-0 w-100 z-index-2",
    bootstrap,
  },

  /* -------------------------------- MUTATIONS ------------------------------- */
  mutations: {
    toggleConfigurator(state) {
      state.showConfig = !state.showConfig;
    },

    navbarMinimize(state) {
      const sidenav_show = document.querySelector(".g-sidenav-show");
      if (sidenav_show.classList.contains("g-sidenav-hidden")) {
        sidenav_show.classList.remove("g-sidenav-hidden");
        sidenav_show.classList.add("g-sidenav-pinned");
        state.isPinned = true;
      } else {
        sidenav_show.classList.add("g-sidenav-hidden");
        sidenav_show.classList.remove("g-sidenav-pinned");
        state.isPinned = false;
      }
    },

    changeLoading(state, payload) {
      state.is_querying_timer = payload;
    },

    sidebarType(state, payload) {
      state.isTransparent = payload;
    },

    cardBackground(state, payload) {
      state.color = payload;
    },

    navbarFixed(state) {
      state.isNavFixed = !state.isNavFixed;
    },

    toggleEveryDisplay(state) {
      state.showNavbar = !state.showNavbar;
      state.showSidenav = !state.showSidenav;
      state.showFooter = !state.showFooter;
    },

    toggleHideConfig(state) {
      state.hideConfigButton = !state.hideConfigButton;
    },

    updateUser(state, user) {
      state.user = user;
    },

    updateToken(state, token) {
      state.token = token;
    },

    updatePermissions(state, permissions) {
      state.permissions = permissions;
    },

    updateCompany(state, company) {
      state.company = company;
    },

    updateCompanyActive(state, companyActive) {
      state.companyActive = companyActive;
    },

    updateLogo(state, logo) {
      state.logo = logo;
    },

    updateTime(state, time) {
      state.time = time;
    },

    updateTtl(state, ttl) {
      state.ttl = ttl;
    },
    updatePackageItems(state, items) {
      state.package_items = items;
    },

    /* ----------------------------- inactivity logout ----------------------------- */
    setInactivityTimer(state, timer) {
      state.inactivityTimer = timer;
    },

    clearInactivityTimer(state) {
      clearTimeout(state.inactivityTimer);
      state.inactivityTimer = null;
    },

    LOGOUT(state) {
      state.token = null;
      state.user = null;
      state.permissions = [];
      state.company = "";
      state.logo = "";
    },
  },

  /* ---------------------------------- ACTIONS ---------------------------------- */
  actions: {
    toggleSidebarColor({ commit }, payload) {
      commit("sidebarType", payload);
    },

    setCardBackground({ commit }, payload) {
      commit("cardBackground", payload);
    },

    updateUser({ commit }, user) {
      commit("updateUser", user);
    },

    updateToken({ commit }, token) {
      commit("updateToken", token);
    },

    updatePermissions({ commit }, permissions) {
      commit("updatePermissions", permissions);
    },

    updateCompany({ commit }, company) {
      commit("updateCompany", company);
    },

    updateCompanyActive({ commit }, companyActive) {
      commit("updateCompanyActive", companyActive);
    },

    updateLogo({ commit }, logo) {
      commit("updateLogo", logo);
    },

    updateTime({ commit }, time) {
      commit("updateTime", time);
    },

    updateTtl({ commit }, ttl) {
      commit("updateTtl", ttl);
    },

    changeLoading({ commit }, status) {
      commit("changeLoading", status);
    },

    updatePackageItems({ commit }, items) {
      commit("updatePackageItems", items);
    },

    /* ------------------------------ token checker ------------------------------ */
    checkToken({ state, commit }) {
      if (!state.token) return false;

      if (isTokenExpired(state.token)) {
        commit("LOGOUT");
        return false;
      }

      return true;
    },

    /* ---------------------------------- logout ---------------------------------- */
    logout({ commit }) {
      commit("LOGOUT");
      storage.clear();
      window.location.href = "/sign-in";
    },
  },

  /* ---------------------------------- GETTERS ---------------------------------- */
  getters: {
    hasPermission: (state) => (permission) => {
      return state.permissions.includes(permission);
    },

    getLastActivityTime(state) {
      return state.is_querying_timer;
    },

    company_is_active(state) {
      return state.companyActive;
    },

    getTtl(state) {
      return state.ttl;
    },

    getTokenGenerationTime(state) {
      return state.time;
    },

    currentCompany(state) {
      return state.company;
    },

    currentLogo(state) {
      return state.logo;
    },

    currentUser(state) {
      return state.user;
    },

    packageItems(state) {
      return state.package_items;
    },

    /* ---------------------------- FIXED GETTER HERE ---------------------------- */
    isAuthenticated(state) {
      // console.log("------searching token----------");
      if (!state.token) return false;
      // console.log("at least some form of token is found");
      // console.log(state.token);

      return !isTokenExpired(state.token);
    },

    currentToken(state) {
      return state.token;
    },
  },

  /* --------------------------------- PERSISTENCE -------------------------------- */
  plugins: [
    createPersistedState({
      storage,
      key: "your-app-state",
      paths: [
        "token",
        "time",
        "user",
        "company",
        "companyActive",
        "logo",
        "permissions",
        "package_items",
      ],
      serializer: {
        write: (state) => JSON.stringify(state),
        read: (s) => JSON.parse(s),
      },
    }),
  ],
});
